import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { User, Phone, CheckCircle, Clock, Fire } from '@phosphor-icons/react';

const getJourneyLabel = (value) => {
  const labels = {
    pronto_atendimento: 'Pronto para Atendimento',
    em_avaliacao: 'Em Avaliação',
    nutricao_futura: 'Nutrição Futura',
    quente: 'Pronto para Atendimento',
    morno: 'Em Avaliação',
    frio: 'Nutrição Futura',
  };
  return labels[value] || value;
};

const getJourneyBadgeClass = (value) => {
  if (value === 'pronto_atendimento' || value === 'quente') return 'bg-emerald-100 text-emerald-700';
  if (value === 'em_avaliacao' || value === 'morno') return 'bg-orange-100 text-orange-700';
  return 'bg-slate-100 text-slate-700';
};


const API = `/api`;

const Corretores = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, {
        withCredentials: true,
      });
      setLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const pendingLeads = leads.filter(
    (l) => l.status === 'novo' || l.status === 'ia_atendendo' || l.status === 'qualificado'
  );
  const inProgressLeads = leads.filter((l) => l.status === 'em_atendimento' || l.status === 'proposta');
  const hotLeads = leads.filter((l) => l.classification === 'quente');

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="corretores-title">
            Meus Leads
          </h1>
          <p className="text-slate-600 mt-1">Gerencie seus leads e oportunidades</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <User size={20} weight="duotone" className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{leads.length}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                <Clock size={20} weight="duotone" className="text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{pendingLeads.length}</p>
                <p className="text-sm text-slate-500">Pendentes</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle size={20} weight="duotone" className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{inProgressLeads.length}</p>
                <p className="text-sm text-slate-500">Em Atendimento</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <Fire size={20} weight="duotone" className="text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{hotLeads.length}</p>
                <p className="text-sm text-slate-500">Quentes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-5 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Todos os Leads</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Telefone
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Empreendimento
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Classificação
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Score
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-slate-900">{lead.name}</p>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <p className="text-sm text-slate-600">{lead.phone}</p>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <p className="text-sm text-slate-600">{lead.enterprise || '-'}</p>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 capitalize">
                        {lead.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      {lead.classification ? (
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-medium uppercase ${
                            lead.classification === 'quente'
                              ? 'bg-red-100 text-red-700'
                              : lead.classification === 'morno'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {getJourneyLabel(lead.classification)}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      {lead.score ? (
                        <span className="text-sm font-medium text-slate-900">{lead.score}</span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Corretores;